import { db } from '../config/firebase';
import { doc, setDoc, serverTimestamp, getDoc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../config/firebase';

interface OnlineUser {
  userId: string;
  userType: string;
  nome: string;
  empresaId?: string;
  ultimaAtualizacao: any;
  online: boolean;
}

/**
 * Serviço para rastrear presença online de usuários
 */
export const presenceService = {
  /**
   * Inicia o rastreamento de presença do usuário atual
   */
  async startPresence(userId: string, userData: { userType: string; nome: string; empresaId?: string }) {
    try {
      const presenceRef = doc(db, 'presence', userId);
      
      // Marcar como online
      await setDoc(presenceRef, {
        userId,
        userType: userData.userType,
        nome: userData.nome,
        empresaId: userData.empresaId || null,
        online: true,
        ultimaAtualizacao: serverTimestamp(),
        conectadoEm: serverTimestamp()
      }, { merge: true });

      // Atualizar timestamp periodicamente (a cada 30 segundos)
      const intervalId = setInterval(async () => {
        try {
          await updateDoc(presenceRef, {
            ultimaAtualizacao: serverTimestamp()
          });
        } catch (error) {
          console.error('Erro ao atualizar presença:', error);
          clearInterval(intervalId);
        }
      }, 30000);

      // Limpar intervalo e marcar como offline quando desconectar
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (!user) {
          clearInterval(intervalId);
          try {
            await updateDoc(presenceRef, {
              online: false,
              desconectadoEm: serverTimestamp()
            });
          } catch (error) {
            console.error('Erro ao marcar como offline:', error);
          }
        }
      });

      // Também marcar como offline quando a página for fechada
      if (typeof window !== 'undefined') {
        window.addEventListener('beforeunload', async () => {
          try {
            await updateDoc(presenceRef, {
              online: false,
              desconectadoEm: serverTimestamp()
            });
          } catch (error) {
            console.error('Erro ao marcar como offline no beforeunload:', error);
          }
        });
      }

      return () => {
        clearInterval(intervalId);
        unsubscribe();
      };
    } catch (error) {
      console.error('Erro ao iniciar presença:', error);
    }
  },

  /**
   * Para o rastreamento de presença
   */
  async stopPresence(userId: string) {
    try {
      const presenceRef = doc(db, 'presence', userId);
      await updateDoc(presenceRef, {
        online: false,
        desconectadoEm: serverTimestamp()
      });
    } catch (error) {
      console.error('Erro ao parar presença:', error);
    }
  },

  /**
   * Verifica se um usuário está online
   */
  async isUserOnline(userId: string): Promise<boolean> {
    try {
      const presenceRef = doc(db, 'presence', userId);
      const presenceDoc = await getDoc(presenceRef);
      
      if (!presenceDoc.exists()) {
        return false;
      }

      const data = presenceDoc.data();
      
      // Considerar online se:
      // 1. online === true
      // 2. última atualização foi há menos de 1 minuto
      if (data.online) {
        const ultimaAtualizacao = data.ultimaAtualizacao?.toDate();
        if (ultimaAtualizacao) {
          const agora = new Date();
          const diferenca = agora.getTime() - ultimaAtualizacao.getTime();
          return diferenca < 60000; // 1 minuto
        }
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Erro ao verificar presença:', error);
      return false;
    }
  }
};

